import { t } from '@/features/i18n/utils';
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useSwitchOrganization } from '@/features/user/mutations';
import { Icon } from '@/ui/components/icon';
import { Building01Icon, PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderUserMenu } from '@/ui/components/header';
import {
  MainActions,
  MainActionsEnd,
  MainActionsSearchInput,
  MainHeader,
  MainScrollArea,
} from '@/ui/components/main';
import { Button } from '@/ui/components/button';
import { H1 } from '@/ui/components/heading';
import { Badge } from '@/ui/components/badge';
import { OrganizationAvatar } from '@/features/organizations/components/organization-avatar';
import { UserRole } from '@/features/user/constants';
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Table, TableBody, TableRow } from '@/ui/components/table';
import { DataTableHeader, DataTableCell, SortableHeader } from '@/ui/components/data-table';

export const Route = createFileRoute('/_authed/settings/organizations')({
  component: RouteComponent,
});

type Organization = ReturnType<typeof useAuth>['user']['organizations'][number];

function RouteComponent() {
  const auth = useAuth();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const switchOrganization = useSwitchOrganization();

  const filteredData = useMemo(() => {
    const orgs = auth.user.organizations;
    if (!searchQuery.trim()) return orgs;

    const query = searchQuery.toLowerCase().trim();
    return orgs.filter((org) => org.organizationName.toLowerCase().includes(query));
  }, [auth.user.organizations, searchQuery]);

  const columns = [
    {
      accessorKey: 'organizationName',
      size: 200,
      meta: { primary: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <OrganizationAvatar
            organization={{ id: row.original.organizationId, name: row.original.organizationName }}
            size={24}
            className="rounded-[calc(var(--radius-md)-0.125rem)]"
          />
          {row.original.organizationName}
          {row.original.organizationId === auth.user.organizationId && (
            <Badge size="xs" variant="tertiary-accent">
              {t({ en: 'Current', nb: 'Nåværende' })}
            </Badge>
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Name', nb: 'Navn' })} />
      ),
    },
    {
      accessorKey: 'role',
      size: 150,
      cell: ({ row }) => (
        <Badge
          size="sm"
          variant={
            row.original.role === UserRole.ORGANIZATION_ADMIN ? 'tertiary-accent' : 'tertiary'
          }
        >
          {row.original.role === UserRole.ORGANIZATION_ADMIN
            ? t({ en: 'Admin', nb: 'Admin' })
            : t({ en: 'Member', nb: 'Medlem' })}
        </Badge>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Role', nb: 'Rolle' })} />
      ),
    },
    {
      accessorKey: 'isEnabled',
      size: 150,
      cell: ({ row }) => (
        <Badge
          size="sm"
          variant={row.original.isEnabled ? 'tertiary-success' : 'tertiary-destructive'}
        >
          {row.original.isEnabled
            ? t({ en: 'Active', nb: 'Aktiv' })
            : t({ en: 'Deactivated', nb: 'Deaktivert' })}
        </Badge>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Status', nb: 'Status' })} />
      ),
    },
  ] satisfies ColumnDef<Organization>[];

  const table = useReactTable({
    columns,
    data: filteredData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
    getRowId: (row) => row.organizationId,
  });

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={Building01Icon} />
            {t({ en: 'Your organisations', nb: 'Dine organisasjoner' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>{t({ en: 'Your organisations', nb: 'Dine organisasjoner' })}</H1>
          <Button nativeButton={false} render={<Link to="/add-organization" />}>
            <Icon icon={PlusSignIcon} />
            {t({ en: 'Create an organisation...', nb: 'Opprett organisasjon...' })}
          </Button>
        </MainHeader>
        <MainActions className="border-t!">
          <MainActionsEnd>
            <MainActionsSearchInput
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
          </MainActionsEnd>
        </MainActions>
        <Table>
          <DataTableHeader table={table} />
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer aria-disabled:cursor-not-allowed data-pending:cursor-wait"
                data-pending={switchOrganization.isPending ? '' : undefined}
                aria-disabled={switchOrganization.isPending || !row.original.isEnabled || undefined}
                onClick={() => {
                  if (!row.original.isEnabled) return;
                  switchOrganization.mutate(row.original.organizationId);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <DataTableCell key={cell.id} cell={cell} />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainScrollArea>
    </>
  );
}
